import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { track } from '../../lib/analytics'
import {
  Sparkles, Target, Compass, BarChart3, Lightbulb, Rocket, Building2,
  Phone, Download, Loader, ExternalLink, CheckCircle2, AlertCircle,
} from 'lucide-react'
import { PLANS, formatUSD, formatCOP } from '../../constants/pricing'

// ── Hard paywall que aparece DESPUÉS del informe final de Etapa 0 ──────
// Estructura:
//   1. Banner de cierre "Tu diagnóstico está listo"
//   2. Preview de las 6 etapas que siguen (con icono + título + 1 línea)
//   3. CTA principal — plan anual (mejor margen para NVC + savings para cliente)
//   4. CTA secundario — plan mensual
//   5. CTA tertiary — "Hablar con un consultor primero" (Calendly o mailto)
//   6. CTA cuaternario — "Descargar informe (PDF borrador)"
//
// Reusable: úsalo en MiModeloNegocio (FinalReportView) y en CajaDashboardPage
// como banner principal cuando etapa0_final_report está generado.
// ────────────────────────────────────────────────────────────────────────

const SIX_ETAPAS = [
  { num: 1, icon: Target,     title: 'Diagnóstico Estratégico',     desc: 'PESTEL · Porter · Análisis Interno · DOFA. La IA cruza tus supuestos con análisis estructurado.' },
  { num: 2, icon: Compass,    title: 'Definición Estratégica',      desc: 'Misión, visión, objetivos y mapa estratégico (BSC) — la dirección hacia donde apuntar.' },
  { num: 3, icon: BarChart3,  title: 'Análisis del Mercado',        desc: 'TAM/SAM/SOM, productos, canales, competencia y perfil del consumidor — con datos reales.' },
  { num: 4, icon: Lightbulb,  title: 'Estrategia por Unidad',       desc: 'Posicionamiento, portafolio, precio y plan de marca por cada mercado/UN priorizado.' },
  { num: 5, icon: Rocket,     title: 'Plan de Implementación',      desc: 'Pilares, actividades, responsables y KPIs trimestrales — lo que pasa de plan a ejecución.' },
  { num: 6, icon: Building2,  title: 'Plan Corporativo',            desc: 'Consolidación final lista para presentar a junta o socios. Word/PDF descargables.' },
]

export default function EtapaFinalPaywall({ report, ficha, consultancyId, companyName }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [exporting, setExporting] = useState(false)

  async function handlePay(planId) {
    setLoading(true); setError('')
    track('caja_final_paywall_clicked', { plan_id: planId })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-mp-preference`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ planId }),
        }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Error al crear el pago')
      window.location.href = import.meta.env.DEV ? data.sandbox_init_point : data.init_point
    } catch (err) {
      setError(err.message ?? 'No se pudo iniciar el pago')
      setLoading(false)
    }
  }

  function handleTalkToConsultant() {
    track('caja_final_paywall_talk_to_consultant_clicked')
    const calendlyUrl = import.meta.env.VITE_CALENDLY_URL
    if (calendlyUrl) {
      window.open(calendlyUrl, '_blank')
    } else {
      // Fallback a mailto con cuerpo pre-cargado
      const subject = encodeURIComponent('Quiero hablar con un consultor antes de pagar')
      const body = encodeURIComponent(
        `Hola,\n\nTerminé mi diagnóstico de Etapa 0${companyName ? ` para ${companyName}` : ''} y antes de activar el plan estratégico, me gustaría hablar con un consultor 15 minutos para resolver dudas.\n\n¿Cuándo podemos coordinar una llamada?\n\nGracias.`
      )
      window.location.href = `mailto:estrategia@nextvalleyco.com?subject=${subject}&body=${body}`
    }
  }

  async function handleDownloadPDF() {
    if (!report) return
    setExporting(true); setError('')
    try {
      // Lazy load jsPDF para no inflar el bundle principal
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ unit: 'mm', format: 'a4' })

      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 18
      const usableW = pageW - margin * 2

      // Header navy con logo
      doc.setFillColor(14, 29, 47) // brand-800 #0E1D2F
      doc.rect(0, 0, pageW, 26, 'F')
      doc.setTextColor(251, 248, 239) // cream-100
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.text('NEXT VALLEY', margin, 14)
      doc.setFontSize(8)
      doc.setTextColor(204, 163, 0) // accent-500
      doc.text('STRATEGIC PARTNERS', margin, 19.5)

      // Watermark BORRADOR
      doc.setTextColor(204, 163, 0)
      doc.setFontSize(7)
      doc.text(`BORRADOR · ETAPA 0 · ${new Date().toLocaleDateString('es-CO')}`, pageW - margin, 14, { align: 'right' })

      let y = 36
      // Título empresa
      doc.setTextColor(14, 29, 47)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text(companyName ?? 'Mi empresa', margin, y)
      y += 6
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(120, 120, 120)
      doc.text('Informe Final · Etapa 0 · Modelo de Negocio', margin, y)
      y += 8

      // Stripe línea
      doc.setDrawColor(204, 163, 0)
      doc.setLineWidth(0.5)
      doc.line(margin, y, pageW - margin, y)
      y += 6

      // Contenido del report (markdown stripped)
      doc.setTextColor(40, 40, 40)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')

      const cleanedReport = report
        .replace(/^#{1,6}\s+/gm, '')              // remove markdown headings prefix
        .replace(/\*\*(.+?)\*\*/g, '$1')           // remove bold
        .replace(/\*(.+?)\*/g, '$1')               // remove italic
        .replace(/`(.+?)`/g, '$1')                 // remove inline code
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')   // remove links

      const lines = doc.splitTextToSize(cleanedReport, usableW)
      const lineHeight = 4.6

      for (const line of lines) {
        if (y > pageH - 25) {
          doc.addPage()
          y = margin + 4
        }
        // Si la línea está vacía, salta menos espacio
        if (!line.trim()) {
          y += lineHeight * 0.5
        } else {
          doc.text(line, margin, y)
          y += lineHeight
        }
      }

      // Footer en última página
      const totalPages = doc.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(7)
        doc.setTextColor(160, 160, 160)
        doc.text(
          'Versión preliminar · Plan estratégico completo disponible al activar suscripción NVC',
          pageW / 2,
          pageH - 8,
          { align: 'center' }
        )
        doc.text(`Página ${i} de ${totalPages}`, pageW - margin, pageH - 8, { align: 'right' })
      }

      const safeName = (companyName ?? 'empresa').replace(/[^a-z0-9]/gi, '_')
      doc.save(`Informe_Etapa0_BORRADOR_${safeName}.pdf`)
      track('caja_final_paywall_pdf_downloaded')
    } catch (err) {
      setError(err.message ?? 'No se pudo generar el PDF')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Header de cierre */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 via-cream-100 to-accent-50/40 border-2 border-emerald-200 px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Diagnóstico cerrado</p>
            <h3 className="text-base font-bold text-gray-900 mt-0.5">Listo. Esto es solo el comienzo.</h3>
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">
              Acabas de completar la etapa más importante del proceso — entender tu modelo de
              negocio actual y mapear lo que sigue. Pero el verdadero valor de la metodología
              NVC viene de las <strong>6 etapas que vienen después</strong>: ahí es donde tus
              supuestos se ponen a prueba con datos reales y el plan toma forma.
            </p>
          </div>
        </div>
      </div>

      {/* Preview de las 6 etapas */}
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-3">Lo que sigue</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SIX_ETAPAS.map(et => (
            <div key={et.num} className="rounded-xl bg-white border border-gray-200 p-4 hover:border-brand-300 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-brand-800 text-accent-400 flex items-center justify-center flex-shrink-0">
                  <et.icon size={17} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] font-bold text-accent-600 tabular-nums">ETAPA {et.num}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 mt-0.5">{et.title}</h4>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{et.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA principal — Plan Anual */}
      <div className="rounded-2xl bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 text-cream-100 p-6 shadow-lg relative overflow-hidden">
        <div className="absolute top-3 right-3">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-accent-500 text-brand-900 px-2.5 py-1 rounded-full">
            Ahorra 33%
          </span>
        </div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent-400 font-bold">Plan Anual · Recomendado</p>
        <p className="text-3xl font-bold mt-2">{formatUSD(PLANS.annual.priceUSD)}<span className="text-sm text-cream-100/60 font-normal">/año</span></p>
        <p className="text-xs text-cream-100/60 mt-1">≈ {formatCOP(PLANS.annual.priceCOP)} · cobrado en COP a TRM del día</p>
        <p className="text-sm text-cream-100/80 mt-3 leading-relaxed">
          Acceso completo a las 6 etapas restantes + descarga del plan en Word/PDF +
          agente IA con contexto de tu proyecto + soporte por email.
        </p>
        <button
          onClick={() => handlePay('annual')}
          disabled={loading}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-brand-900 font-bold text-base px-6 py-3.5 rounded-xl transition-colors shadow"
        >
          {loading
            ? <><Loader size={16} className="animate-spin" /> Redirigiendo a MercadoPago…</>
            : <><Sparkles size={16} /> Activar mi plan anual <ExternalLink size={14} /></>}
        </button>
      </div>

      {/* CTA secundario — Plan mensual */}
      <div className="rounded-xl bg-white border border-gray-200 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Plan Mensual</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">{formatUSD(PLANS.monthly.priceUSD)}<span className="text-xs text-gray-400 font-normal">/mes</span></p>
          <p className="text-[11px] text-gray-500">≈ {formatCOP(PLANS.monthly.priceCOP)}/mes · cancela cuando quieras</p>
        </div>
        <button
          onClick={() => handlePay('monthly')}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-900 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap"
        >
          {loading ? <Loader size={13} className="animate-spin" /> : <ExternalLink size={13} />}
          Activar mensual
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {/* Secondary actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={handleTalkToConsultant}
          className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-brand-300 text-gray-700 text-sm font-semibold px-4 py-3 rounded-xl transition-colors"
        >
          <Phone size={14} /> Hablar con un consultor primero
        </button>
        <button
          onClick={handleDownloadPDF}
          disabled={exporting || !report}
          className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-brand-300 disabled:opacity-50 text-gray-700 text-sm font-semibold px-4 py-3 rounded-xl transition-colors"
        >
          {exporting
            ? <><Loader size={14} className="animate-spin" /> Generando PDF…</>
            : <><Download size={14} /> Descargar informe (PDF · BORRADOR)</>}
        </button>
      </div>
      <p className="text-[11px] text-gray-400 text-center">
        El PDF descargado lleva marca de "BORRADOR · ETAPA 0". El plan completo
        sin marca se descarga al activar la suscripción.
      </p>
    </div>
  )
}
